import { inject, Injectable } from '@angular/core';
import { AbstractAuthService } from '@zero/shared/util-abstract-services';
import { CustomerStateFacade } from '@zero/shared/util-global-state';
import { toZonedTime } from 'date-fns-tz';
import { environment } from 'environments/environment';
import { BehaviorSubject, catchError, combineLatest, firstValueFrom, map, Observable, of, switchMap, tap } from 'rxjs';
import { PortfolioTransfer } from '../entities/depot-transfer.entity';
import { MajorelPostData } from '../entities/majorel.entity';
import { DepotTransferInfrastructure } from '../infrastructure/depot-transfer.infrastructure';

@Injectable({ providedIn: 'root' })
export class DepotTransferFacade {
  #postData = new BehaviorSubject<MajorelPostData>(undefined);
  public loading$: Observable<boolean>;

  public portfolioTransfers: PortfolioTransfer[] = [];
  public readonly postData$ = this.#postData.asObservable();
  public hasLoadingFormError = false;
  public isLoadingForm = false;
  public isFeatureActive?: boolean;
  public isAllowedToTransferPortfolio: boolean;
  public majorelUrl: string = environment.portfolioTransferUrl;
  public isMaintenanceTime = false;

  readonly #authService = inject(AbstractAuthService);
  readonly #depotTransferInfrastructure = inject(DepotTransferInfrastructure);
  readonly #customerStateFacade = inject(CustomerStateFacade);

  public setup = async (): Promise<void> => {
    this.isLoadingForm = true;

    try {
      this.isAllowedToTransferPortfolio = await this.#isCustomer();
      if (!this.isAllowedToTransferPortfolio) {
        this.isLoadingForm = false;
        return;
      }
    } catch {
      this.hasLoadingFormError = true;
      this.isLoadingForm = false;
      return;
    }

    const isFeatureActive$ = this.#depotTransferInfrastructure.getIsFeatureEnabled();
    const baseData$ = this.#depotTransferInfrastructure.getBaseData();
    const portfolioTransfers$ = this.#depotTransferInfrastructure.getPortfolioTransfers();

    await firstValueFrom(
      combineLatest([isFeatureActive$, baseData$, portfolioTransfers$]).pipe(
        map(([isFeatureActive, baseData, portfolioTransfers]) => ({
          isFeatureActive,
          baseData,
          portfolioTransfers,
        })),
        tap(({ isFeatureActive, baseData, portfolioTransfers }) => {
          this.isFeatureActive = isFeatureActive;
          this.portfolioTransfers = portfolioTransfers;
          this.isMaintenanceTime = this.#isMajorelDown();
          if (!this.isFeatureActive || this.isMaintenanceTime) {
            this.isLoadingForm = false;
            return;
          }

          if (baseData) {
            this.#depotTransferInfrastructure.postHash(baseData).subscribe({
              next: ({ signature, data }) => this.#postData.next(new MajorelPostData(data, signature)),
              error: () => this.#handleError(),
              complete: () => this.#finishLoading(),
            });
          } else {
            this.#handleError();
          }
        }),
        catchError(() => {
          this.#handleError();
          return of();
        }),
      ),
    );
  };

  #isMajorelDown = (): boolean => {
    const now = toZonedTime(new Date(), 'Europe/Berlin');

    // downtime is between Wednesday 10:00pm CEST and Thursday 03:00am CEST
    const isWednesday = now.getDay() === 3;
    if (isWednesday) {
      return now.getHours() > 20; // with some buffer
    }

    const isThursday = now.getDay() === 4;
    if (isThursday) {
      return now.getHours() < 4; // with some buffer
    }

    return false;
  };

  #isCustomer() {
    return firstValueFrom(
      this.#authService.isCustomer$().pipe(
        switchMap(() => this.#customerStateFacade.loadCustomerBaseData()),
        switchMap(() => this.#customerStateFacade.customerBaseData$),
        map((baseData) => Boolean(baseData.fzContractActiveDate)),
      ),
    );
  }

  #finishLoading = (): void => {
    this.isLoadingForm = false;
    this.hasLoadingFormError = false;
  };

  #handleError = (): void => {
    this.isLoadingForm = false;
    this.hasLoadingFormError = true;
  };
}
